/**
 * FundingSourcesStep — Step 4 of setup wizard (healthcare products only).
 *
 * Shows payer programs applicable to the selected product + jurisdiction.
 * Selecting payers can activate additional RuleSets (e.g., Medicaid → 245D overlay).
 *
 * Refined from the old PayersStep to show RuleSet overlay impact.
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Alert,
  ThemeIcon,
  Checkbox,
  SimpleGrid,
  Divider,
  Tooltip,
} from '@mantine/core';
import {
  IconCurrencyDollar,
  IconInfoCircle,
  IconPlus,
  IconShieldCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import type { ProductDefinition } from './ProductSelectionStep';
import type { Payer } from '../hooks/useComposition';

export interface FundingSourcesStepProps {
  product: ProductDefinition;
  selectedPayers: Payer[];
  onPayersChange: (payers: Payer[]) => void;
}

interface PayerOption {
  id: Payer;
  label: string;
  description: string;
  requiresEnrollment: boolean;
  additionalRulesets?: string[];
  rulesetImpact?: string;
}

/**
 * Payer programs with RuleSet overlay impact.
 * When a payer is selected, it may activate additional RuleSets
 * beyond the product's base requirements.
 */
const PAYER_OPTIONS: PayerOption[] = [
  {
    id: 'private_pay',
    label: 'Private Pay',
    description: 'Clients/families pay out of pocket. Simplest regulatory profile.',
    requiresEnrollment: false,
  },
  {
    id: 'medicaid_ew',
    label: 'Medicaid EW (Elderly Waiver)',
    description: 'Minnesota Medicaid waiver for elderly care. Lead agency coordination required.',
    requiresEnrollment: true,
    additionalRulesets: ['rs-4-1-001'],
    rulesetImpact: 'Activates MN 245D HCBS overlay — adds 18 rules, 10 workflows including CSSP, MnCHOICES assessment',
  },
  {
    id: 'medicaid_cadi',
    label: 'Medicaid CADI',
    description: 'Community Alternatives for Disabled Individuals waiver program.',
    requiresEnrollment: true,
    additionalRulesets: ['rs-4-1-001'],
    rulesetImpact: 'Activates MN 245D HCBS overlay — CADI waiver services require lead agency CSSP coordination',
  },
  {
    id: 'medicaid_dd',
    label: 'Medicaid DD Waiver',
    description: 'Developmental Disabilities waiver for specialized residential services.',
    requiresEnrollment: true,
    additionalRulesets: ['rs-4-1-001'],
    rulesetImpact: 'Activates MN 245D HCBS overlay — DD waiver includes additional behavior support documentation',
  },
  {
    id: 'medicare',
    label: 'Medicare',
    description: 'Federal Medicare program. Time-limited skilled stays with MDS assessment.',
    requiresEnrollment: true,
    additionalRulesets: ['rs-4-0-002'],
    rulesetImpact: 'Activates CMS Conditions of Participation — MDS assessment, minimum data set tracking',
  },
  {
    id: 'va',
    label: 'VA (Veterans Affairs)',
    description: 'Veterans Affairs healthcare benefits. Separate enrollment and billing process.',
    requiresEnrollment: true,
  },
  {
    id: 'commercial_insurance',
    label: 'Commercial Insurance',
    description: 'Private health insurance providers. Individual payer contracts required.',
    requiresEnrollment: true,
  },
];

export function FundingSourcesStep({
  product,
  selectedPayers,
  onPayersChange,
}: FundingSourcesStepProps) {
  const togglePayer = (payerId: Payer) => {
    if (selectedPayers.includes(payerId)) {
      onPayersChange(selectedPayers.filter((p) => p !== payerId));
    } else {
      onPayersChange([...selectedPayers, payerId]);
    }
  };

  // Calculate additional RuleSets from payer selections
  const additionalRulesets = useMemo(() => {
    const rulesets = new Set<string>();
    for (const payerId of selectedPayers) {
      const option = PAYER_OPTIONS.find((p) => p.id === payerId);
      if (option?.additionalRulesets) {
        for (const rs of option.additionalRulesets) {
          // Only count if not already in product's base requirements
          if (!product.requiredRulesets.includes(rs)) {
            rulesets.add(rs);
          }
        }
      }
    }
    return Array.from(rulesets);
  }, [selectedPayers, product.requiredRulesets]);

  const payersWithEnrollment = selectedPayers.filter(
    (p) => PAYER_OPTIONS.find((o) => o.id === p)?.requiresEnrollment
  );

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" size="lg" color="teal">
          <IconCurrencyDollar size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="lg">Funding Sources</Text>
          <Text size="sm" c="dimmed">
            Select which payment programs your facility accepts.
            Some payers activate additional regulatory requirements.
          </Text>
        </div>
      </Group>

      {/* Summary */}
      <Group gap="xs">
        <Badge size="md" variant="light" color="teal">
          {selectedPayers.length} selected
        </Badge>
        {payersWithEnrollment.length > 0 && (
          <Badge size="sm" variant="outline" color="orange">
            {payersWithEnrollment.length} require enrollment
          </Badge>
        )}
        {additionalRulesets.length > 0 && (
          <Badge size="sm" variant="outline" color="red" leftSection={<IconPlus size={10} />}>
            +{additionalRulesets.length} additional ruleset{additionalRulesets.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </Group>

      {/* Payer Cards */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {PAYER_OPTIONS.map((option) => {
          const isSelected = selectedPayers.includes(option.id);
          const hasOverlay = option.additionalRulesets && option.additionalRulesets.length > 0;
          const overlayAlreadyIncluded = option.additionalRulesets?.every(
            (rs) => product.requiredRulesets.includes(rs)
          );

          return (
            <Paper
              key={option.id}
              p="sm"
              withBorder
              shadow={isSelected ? 'sm' : 'xs'}
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? 'var(--mantine-color-teal-5)' : undefined,
                borderWidth: isSelected ? 2 : 1,
                transition: 'all 150ms ease',
              }}
              onClick={() => togglePayer(option.id)}
            >
              <Stack gap="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => togglePayer(option.id)}
                      size="xs"
                      styles={{ input: { cursor: 'pointer' } }}
                    />
                    <Text size="sm" fw={600}>
                      {option.label}
                    </Text>
                  </Group>
                  {option.requiresEnrollment && (
                    <Tooltip label="Requires enrollment with payer">
                      <Badge size="xs" color="orange" variant="light">
                        Enrollment
                      </Badge>
                    </Tooltip>
                  )}
                </Group>

                <Text size="xs" c="dimmed">
                  {option.description}
                </Text>

                {/* RuleSet overlay impact */}
                {hasOverlay && !overlayAlreadyIncluded && (
                  <Paper p="xs" bg={isSelected ? 'red.0' : 'gray.0'} radius="sm">
                    <Group gap="xs" wrap="nowrap">
                      {isSelected ? (
                        <IconShieldCheck size={14} color="var(--mantine-color-red-6)" />
                      ) : (
                        <IconAlertTriangle size={14} color="var(--mantine-color-gray-5)" />
                      )}
                      <Text size="xs" c={isSelected ? 'red.7' : 'dimmed'}>
                        {option.rulesetImpact}
                      </Text>
                    </Group>
                  </Paper>
                )}

                {hasOverlay && overlayAlreadyIncluded && (
                  <Group gap="xs">
                    <IconShieldCheck size={12} color="var(--mantine-color-green-5)" />
                    <Text size="xs" c="green.7">
                      RuleSet overlay already included in product
                    </Text>
                  </Group>
                )}
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* Info about payer impact */}
      {selectedPayers.length > 0 && (
        <>
          <Divider />
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              <Text span fw={600}>{selectedPayers.length}</Text> funding source{selectedPayers.length !== 1 ? 's' : ''} selected.
              {payersWithEnrollment.length > 0 && (
                <>
                  {' '}<Text span fw={600}>{payersWithEnrollment.length}</Text> require{payersWithEnrollment.length === 1 ? 's' : ''} enrollment
                  with the payer program before billing can begin.
                </>
              )}
              {additionalRulesets.length > 0 && (
                <>
                  {' '}This selection adds <Text span fw={600}>{additionalRulesets.length}</Text> additional
                  regulatory ruleset{additionalRulesets.length !== 1 ? 's' : ''} beyond the product baseline.
                </>
              )}
            </Text>
          </Alert>
        </>
      )}
    </Stack>
  );
}
